// ShopTutorial — first-time guided read-through tour of the shop.
//
// A portal-mounted spotlight overlay: dims the shop, cuts a hole around
// the current step's target element, and floats a paper callout bubble
// beside it. Read-through only — it never requires the player to act,
// and clicking the dim scrim does nothing (explicit buttons only).
//
// Layout-aware: derives desktop/mobile/fold the same way Shop does, so
// per-layout selectors resolve to whatever fork is actually mounted.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { createPortal } from 'react-dom'
import { useIsMobile, useIsFoldOpen } from './useIsMobile'
import { playSfx } from '../sound'
import {
  SHOP_TUTORIAL_STEPS,
  resolveStepTarget,
  type Placement,
  type ShopLayout,
} from './shopTutorial'
import './ShopTutorial.css'

interface ShopTutorialProps {
  open: boolean
  onClose: (reason: 'done' | 'skip') => void
}

/** Padding around the spotlighted element's rect, in px. */
const HOLE_PAD = 8
/** Gap between the spotlight rect and the callout bubble, in px. */
const BUBBLE_GAP = 16
/** Viewport margin the bubble is clamped within, in px. */
const EDGE = 12
/** Assumed bubble box for placement math before/over measurement. */
const BUBBLE_W = 320
const BUBBLE_H = 216

interface Box {
  left: number
  top: number
  width: number
  height: number
}

export function ShopTutorial({ open, onClose }: ShopTutorialProps): ReactElement | null {
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()
  const layout: ShopLayout = foldOpen ? 'fold' : isMobile ? 'mobile' : 'desktop'

  const [stepIndex, setStepIndex] = useState(0)
  const [rect, setRect] = useState<Box | null>(null)
  // Measured bubble size — the scroll-art bubble's height varies with
  // copy length, so we measure the rendered box and feed it back into
  // placement rather than trusting a static estimate (which could let a
  // long bubble clip on a short viewport).
  const [bubbleSize, setBubbleSize] = useState({ w: BUBBLE_W, h: BUBBLE_H })
  const bubbleRef = useRef<HTMLDivElement | null>(null)
  const primaryBtnRef = useRef<HTMLButtonElement | null>(null)

  const total = SHOP_TUTORIAL_STEPS.length
  const step = SHOP_TUTORIAL_STEPS[stepIndex] ?? SHOP_TUTORIAL_STEPS[0]!
  const isLast = stepIndex === total - 1
  const { selector, placement } = resolveStepTarget(step, layout)

  // Reset to the first step whenever the tour (re)opens.
  useEffect(() => {
    if (open) setStepIndex(0)
  }, [open])

  // Resolve the current target's rect; recompute on step/layout change
  // and on resize/scroll. Game screens don't scroll, but resize is real
  // (orientation, fold/unfold, devtools) and keeps the hole aligned.
  useLayoutEffect(() => {
    if (!open) return
    const measure = () => {
      if (!selector) {
        setRect(null)
        return
      }
      const el = document.querySelector(selector)
      if (!el) {
        setRect(null)
        return
      }
      const r = el.getBoundingClientRect()
      // A zero-size element, or one parked outside the viewport (e.g. a
      // collapsed tray or the selection-gated sell ticket that slides in
      // from off-screen), reads as no spotlight → centered fallback,
      // rather than a hole pointing off-screen.
      const onScreen =
        r.right > 0 && r.bottom > 0 && r.left < window.innerWidth && r.top < window.innerHeight
      if (r.width < 1 || r.height < 1 || !onScreen) {
        setRect(null)
        return
      }
      setRect({ left: r.left, top: r.top, width: r.width, height: r.height })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, selector, stepIndex, layout])

  const next = useCallback(() => {
    if (isLast) {
      onClose('done')
      return
    }
    playSfx('ui-tap')
    setStepIndex((i) => Math.min(i + 1, total - 1))
  }, [isLast, onClose, total])

  const back = useCallback(() => {
    playSfx('ui-tap')
    setStepIndex((i) => Math.max(i - 1, 0))
  }, [])

  const skip = useCallback(() => onClose('skip'), [onClose])

  // Keyboard nav + focus the primary button on each step.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault()
        next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        back()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        skip()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, next, back, skip])

  useEffect(() => {
    if (open) primaryBtnRef.current?.focus()
  }, [open, stepIndex])

  // Measure the rendered bubble so placement reserves its true size.
  // Converges in one extra render (width is fixed, height depends only
  // on copy, not on position).
  useLayoutEffect(() => {
    if (!open) return
    const el = bubbleRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setBubbleSize((prev) =>
      Math.abs(prev.w - r.width) < 1 && Math.abs(prev.h - r.height) < 1
        ? prev
        : { w: r.width, h: r.height },
    )
  }, [open, stepIndex, layout, rect])

  if (!open) return null

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720
  const bubblePos = computeBubblePos(rect, placement, vw, vh, bubbleSize.w, bubbleSize.h)
  const hole = rect
    ? {
        x: rect.left - HOLE_PAD,
        y: rect.top - HOLE_PAD,
        w: rect.width + HOLE_PAD * 2,
        h: rect.height + HOLE_PAD * 2,
      }
    : null

  const overlay = (
    <div className="shoptut-root" role="dialog" aria-modal="true" aria-label="Shop tutorial">
      <svg className="shoptut-svg" width="100%" height="100%" aria-hidden="true">
        <defs>
          <mask id="shoptut-hole">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hole && (
              <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx="14" fill="black" />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          className="shoptut-scrim"
          mask="url(#shoptut-hole)"
        />
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx="14"
            className="shoptut-ring"
          />
        )}
      </svg>

      <div
        ref={bubbleRef}
        className={`shoptut-bubble${bubblePos.centered ? ' shoptut-bubble--center' : ''}`}
        style={bubblePos.centered ? undefined : { left: bubblePos.left, top: bubblePos.top }}
      >
        <div className="shoptut-bubble__count tiny-cap">
          {stepIndex + 1} / {total}
        </div>
        <h3 className="cute-title shoptut-bubble__title">{step.title}</h3>
        <p className="hand-note shoptut-bubble__body">{step.body}</p>
        <div className="shoptut-bubble__actions">
          <button type="button" className="cute-btn shoptut-bubble__skip" onClick={skip}>
            {isLast ? 'Close' : 'Skip'}
          </button>
          <div className="shoptut-bubble__nav">
            {stepIndex > 0 && (
              <button type="button" className="cute-btn" onClick={back}>
                Back
              </button>
            )}
            <button
              ref={primaryBtnRef}
              type="button"
              className="cute-btn dark"
              onClick={next}
            >
              {isLast ? 'Got it' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}

/** Position the callout: start from the preferred side, flip to the
 *  opposite side if it doesn't fit, fall back to centered if neither
 *  side has room. Always clamped inside the viewport so it never
 *  overflows (game screens never scroll). */
function computeBubblePos(
  rect: Box | null,
  placement: Placement,
  vw: number,
  vh: number,
  bw: number,
  bh: number,
): { centered: boolean; left: number; top: number } {
  if (!rect || placement === 'center') return { centered: true, left: 0, top: 0 }
  // `placement` is now narrowed to the four directional sides.

  const clampX = (x: number) => Math.max(EDGE, Math.min(x, vw - bw - EDGE))
  const clampY = (y: number) => Math.max(EDGE, Math.min(y, vh - bh - EDGE))

  type Side = 'top' | 'bottom' | 'left' | 'right'
  const fits: Record<Side, boolean> = {
    bottom: vh - (rect.top + rect.height) >= bh + BUBBLE_GAP + EDGE,
    top: rect.top >= bh + BUBBLE_GAP + EDGE,
    right: vw - (rect.left + rect.width) >= bw + BUBBLE_GAP + EDGE,
    left: rect.left >= bw + BUBBLE_GAP + EDGE,
  }
  const opposite: Record<Side, Side> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  }

  let side: Side = placement
  if (!fits[side]) {
    const flip = opposite[side]
    if (fits[flip]) side = flip
    else return { centered: true, left: 0, top: 0 }
  }

  switch (side) {
    case 'bottom':
      return {
        centered: false,
        left: clampX(rect.left + rect.width / 2 - bw / 2),
        top: clampY(rect.top + rect.height + BUBBLE_GAP),
      }
    case 'top':
      return {
        centered: false,
        left: clampX(rect.left + rect.width / 2 - bw / 2),
        top: clampY(rect.top - bh - BUBBLE_GAP),
      }
    case 'right':
      return {
        centered: false,
        left: clampX(rect.left + rect.width + BUBBLE_GAP),
        top: clampY(rect.top + rect.height / 2 - bh / 2),
      }
    case 'left':
      return {
        centered: false,
        left: clampX(rect.left - bw - BUBBLE_GAP),
        top: clampY(rect.top + rect.height / 2 - bh / 2),
      }
    default:
      return { centered: true, left: 0, top: 0 }
  }
}
